import { Alert } from '@/components/ui/alert';
import { Avatar, brandAvatarTint } from '@/components/ui/avatar';
import {
  Box,
  Button,
  CheckboxCard,
  Heading,
  HStack,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import type { McpConsentDecision, McpConsentResponse } from '@fluenceur/types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check } from 'iconoir-react';
import { ShortLogo } from '@fluenceur/ui';
import { McpClientLogo } from './McpClientLogo';

export function McpConsent({
  consent,
  onDecision,
}: {
  consent: McpConsentResponse;
  onDecision: (decision: McpConsentDecision) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const organization = consent.organizations.length === 1 ? consent.organizations[0] : undefined;
  const organizationId = organization?.id ?? '';
  let clientHostname: string | undefined;
  if (consent.client.registrationMethod === 'cimd') {
    try {
      const clientUrl = new URL(consent.client.id);
      if (clientUrl.protocol === 'https:') clientHostname = clientUrl.hostname;
    } catch {
      // Invalid identifiers are rejected by the server; keep rendering its error/expiry state.
    }
  }
  const [brandIds, setBrandIds] = useState<string[]>([]);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const submitting = useRef(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);
  const expired =
    !Number.isFinite(Date.parse(consent.expiresAt)) || now >= Date.parse(consent.expiresAt);
  const submit = async (decision: McpConsentDecision) => {
    if (submitting.current || expired) return;
    submitting.current = true;
    setPending(true);
    setFailed(false);
    try {
      await onDecision(decision);
      // Keep the form locked while the browser leaves for the validated callback.
    } catch {
      submitting.current = false;
      setPending(false);
      setFailed(true);
    }
  };
  return (
    <Stack gap={6}>
      <Box textAlign="center">
        <HStack gap={4} mb={5} justify="center" aria-hidden="true">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            boxSize={12}
            borderWidth="1px"
            borderRadius="xl"
            bg="bg.subtle"
          >
            <ShortLogo height="24px" />
          </Box>
          <ArrowRight width={20} height={20} />
          <McpClientLogo provider={consent.client.provider} />
        </HStack>
        <Heading as="h1" size="xl">
          {t('mcp.consent.title', { client: consent.client.name })}
        </Heading>
        {clientHostname && (
          <Text color="fg.muted" fontSize="sm" mt={2} overflowWrap="anywhere">
            {t('mcp.consent.requestedBy', { host: clientHostname })}
          </Text>
        )}
        <Text color="fg.muted" mt={3} fontSize="sm" maxW="440px" mx="auto">
          {t('mcp.consent.subtitle')}
        </Text>
        <Text
          mt={4}
          fontSize="sm"
          bg="green.subtle"
          color="green.fg"
          borderWidth="1px"
          borderColor="green.muted"
          borderRadius="md"
          px={3}
          py={2}
          overflowWrap="anywhere"
        >
          {t('mcp.consent.account', { name: consent.user.fullName, email: consent.user.email })}
        </Text>
        {organization && (
          <Text color="fg.muted" fontSize="sm" mt={2} overflowWrap="anywhere">
            {t('mcp.consent.organizationLine', { name: organization.name })}
          </Text>
        )}
      </Box>
      {expired && (
        <Alert status="error" title={t('mcp.consent.expired')}>
          {t('mcp.consent.restart')}
        </Alert>
      )}
      {failed && (
        <Alert status="error" title={t('mcp.consent.decisionError')}>
          {t('mcp.consent.restart')}
        </Alert>
      )}
      {!organization && <Text>{t('mcp.consent.noBrands')}</Text>}
      <Box as="fieldset" border="none" p={0}>
        <Text as="legend" fontWeight="semibold" mb={3}>
          {t('mcp.consent.brands')}
        </Text>
        <Text color="fg.muted" mb={3}>
          {t('mcp.consent.selectBrands')}
        </Text>
        <SimpleGrid
          columns={{ base: 1, sm: (organization?.brands.length ?? 0) > 1 ? 2 : 1 }}
          gap={3}
        >
          {organization?.brands.map((brand) => (
            <CheckboxCard.Root
              key={brand.id}
              borderRadius="lg"
              checked={brandIds.includes(brand.id)}
              disabled={pending || expired}
              onCheckedChange={({ checked }) =>
                setBrandIds((current) =>
                  checked === true
                    ? [...current, brand.id]
                    : current.filter((id) => id !== brand.id)
                )
              }
            >
              <CheckboxCard.HiddenInput />
              <CheckboxCard.Control alignItems="center">
                <Box aria-hidden="true" flexShrink={0}>
                  <Avatar
                    src={brand.imageUrl || undefined}
                    name={brand.name}
                    size="sm"
                    rounded="lg"
                    {...brandAvatarTint}
                  />
                </Box>
                <CheckboxCard.Content>
                  <CheckboxCard.Label>{brand.name}</CheckboxCard.Label>
                  {brand.role === 'viewer' && (
                    <Text color="fg.muted" fontSize="sm" mt={1}>
                      {t('mcp.consent.viewer')}
                    </Text>
                  )}
                </CheckboxCard.Content>
                <CheckboxCard.Indicator />
              </CheckboxCard.Control>
            </CheckboxCard.Root>
          ))}
        </SimpleGrid>
      </Box>
      <Box borderTopWidth="1px" pt={5}>
        <Heading size="sm" mb={3}>
          {t('mcp.consent.capabilities')}
        </Heading>
        <Stack as="ul" listStyleType="none" gap={3}>
          {consent.requestedScopes.map((scope) => (
            <HStack as="li" key={scope} gap={3} align="start" fontSize="sm">
              <Box flexShrink={0} pt="2px" color="fg.muted" aria-hidden="true">
                <Check width={16} height={16} />
              </Box>
              <Text>{t(`mcp.scopes.${scope.replace(':', '_')}`)}</Text>
            </HStack>
          ))}
        </Stack>
        <Text fontSize="sm" mt={4}>
          {t('mcp.consent.approvalNotice')}
        </Text>
        <Text color="fg.muted" fontSize="sm" mt={3}>
          {t('mcp.consent.permissionsFixed')}
        </Text>
      </Box>
      <Stack direction={{ base: 'column', sm: 'row-reverse' }} gap={3} pt={1}>
        <Button
          flex={1}
          size="lg"
          disabled={pending || expired || !organization || brandIds.length === 0}
          loading={pending}
          onClick={() => void submit({ decision: 'allow', organizationId, brandIds })}
        >
          {t('mcp.consent.allow')}
        </Button>
        <Button
          variant="ghost"
          flex={1}
          size="lg"
          disabled={pending || expired}
          onClick={() => void submit({ decision: 'deny' })}
        >
          {t('mcp.consent.deny')}
        </Button>
      </Stack>
    </Stack>
  );
}
